import { useEffect, useState, useCallback } from "react";
import API_ROUTES from "~/api-routes";
import useDebounce from "~/hooks/useDebounce";
import apiService from "~/services/service";
import z from "zod";
import { fetchServices } from "~/components/global-entities/globalEntitiesSlice";
import { useDispatch, useSelector } from "react-redux";
import { getStatusOptions, StatusKey } from "~/utils/status";

const useMyProcesses = ({ allEntities }) => {
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(10);
  const [isDataAvailable, setIsDataAvailable] = useState(true);
  const [totalRecord, setTotalRecords] = useState(0);
  const [sortMeta, setSortMeta] = useState([{ field: "name", order: 1 }]);
  const [accessServices, setAccessServices] = useState([]);
  const [selectedValues, setSelectedValues] = useState({
    status: [],
    service_line: [],
    flagged: [],
  });
  const [chipData, setChipData] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({
    status: [],
    service_line: [],
    flagged: [],
  });
  const { access, role: userRole } = useSelector((state) => state.login);
  const [serviceSubOptions, setServiceSubOptions] = useState([]);
  const [search, setSearch] = useState("");
  const debouncedSearchTerm = useDebounce(search, 500);

  const [period, setPeriod] = useState("all");

  // Compute ISO dates based on the selected period; be defensive about invalid inputs
  const computeDates = () => {
    // Support both primitive value and full option object with { code }
    const periodValue: unknown =
      period && typeof period === "object" && (period as any).code !== undefined
        ? (period as any).code
        : period;

    if (
      periodValue === "all" ||
      periodValue === undefined ||
      periodValue === null
    ) {
      return { from_date: "", to_date: "" };
    }

    // Coerce to a positive integer number of days; return empty if not valid
    const daysRaw =
      typeof periodValue === "number"
        ? periodValue
        : parseInt(String(periodValue), 10);
    const days = Number.isFinite(daysRaw) ? daysRaw : NaN;
    if (!Number.isFinite(days) || days <= 0) {
      return { from_date: "", to_date: "" };
    }

    try {
      const now = new Date();
      const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
      if (isNaN(from.getTime()) || isNaN(now.getTime())) {
        return { from_date: "", to_date: "" };
      }
      const fromISO = from.toISOString();
      const toISO = now.toISOString();
      return { from_date: fromISO, to_date: toISO };
    } catch (err) {
      console.error("[MyProcesses] computeDates error", { period, err });
      return { from_date: "", to_date: "" };
    }
  };

  const [filter, setFilter] = useState({
    status: [],
    service_line: [],
    flagged: [],
  });
  const statusOption = getStatusOptions([
    StatusKey.IN_PROGRESS,
    StatusKey.COMPLETED,
    StatusKey.OVERDUE,
    StatusKey.NOT_STARTED,
  ]);
  const entityservices = useSelector(
    (state) => state.globalEntities.entityservices,
  );
  useEffect(() => {
    if (entityservices) {
      const safeAccess = Array.isArray(access) ? access : [];
      const uniqueServices = [
        ...new Set(safeAccess.flatMap((item) => item.services)),
      ];
      const accessService =
        userRole !== "GXM_ADMIN"
          ? entityservices.filter((service) =>
              uniqueServices.includes(service.id),
            )
          : entityservices;

      const serviceSubOptions = accessService?.map((data) => {
        return {
          name: data?.name,
          code: data?.id,
        };
      });
      setServiceSubOptions(serviceSubOptions);
      setAccessServices(accessService);
    }
  }, [entityservices]);

  useEffect(() => {
    dispatch(fetchServices());
  }, []);

  const handleChipData = (selectedValues) => {
    let chipArray = [];
    for (const key in selectedValues) {
      selectedValues[key].map((data) => {
        chipArray.push({ category: key, value: data });
      });
    }
    setChipData(chipArray);
  };
  const fetchData = useCallback(
    async ({
      pageIndex = page,
      pageSize = rows,
      sortBy = sortMeta?.[0]?.field ?? "name",
      sortOrder = sortMeta?.[0]?.order === 1 ? "ASC" : "DESC",
      status = selectedValues?.status?.join(",") || "",
      service = selectedValues?.service_line?.join(",") || "",
      flagged = ((selectedValues?.flagged as string[]) || []).includes("true")
        ? "true"
        : "",
      searchValue = search || "",
    } = {}) => {
      setLoading(true);
      setIsDataAvailable(true);

      try {
        const { from_date, to_date } = computeDates();
        console.log("[MyProcesses] fetchData()", {
          pageIndex,
          pageSize,
          sortBy,
          sortOrder,
          status,
          service,
          flagged,
          searchValue,
          period,
          from_date,
          to_date,
        });

        const { data } = await apiService(
          API_ROUTES.FETCH_USER_PROCESSES,
          "GET",
          {
            replace: {
              page: pageIndex + 1,
              pageSize,
              sortBy,
              sortOrder,
              status,
              service,
              flagged,
              search: encodeURIComponent(searchValue),
              from_date: from_date ? encodeURIComponent(from_date) : "",
              to_date: to_date ? encodeURIComponent(to_date) : "",
            },
            schema: z.object({
              page: z.number().optional(),
              pageSize: z.number().optional(),
              sortBy: z
                .enum(["name", "entity", "service", "due_date"])
                .optional(),
              sortOrder: z.enum(["ASC", "DESC"]).optional(),
              status: z.string().optional(),
              service: z.string().optional(),
              flagged: z.string().optional(),
              search: z.string().optional(),
              from_date: z.string().optional(),
              to_date: z.string().optional(),
            }),
          },
        );
        // Handle both old format (records/totalRecords) and new format (processes/pagination)
        const records = data?.records || data?.processes || [];
        const totalRecords = data?.totalRecords ?? data?.pagination?.total ?? 0;

        console.log("[MyProcesses] fetchData success", {
          totalRecords: totalRecords,
          records: records?.length,
        });
        setTotalRecords(totalRecords);
        if (records?.length) {
          setData(records);
          setIsDataAvailable(true);
        } else {
          if (searchValue.length > 0) {
            setIsDataAvailable(false);
          } else {
            const hasFilters =
              status.length > 0 || service.length > 0 || flagged === "true";
            setIsDataAvailable(hasFilters);
          }
          setData([]);
        }
      } catch (error) {
        console.error("[MyProcesses] fetchData error", error);
        setData([]);
        setIsDataAvailable(false);
      } finally {
        setLoading(false);
      }
    },
    [page, rows, sortMeta, period],
  );

  useEffect(() => {
    fetchData({
      pageIndex: page,
      pageSize: rows,
      service: selectedValues?.service_line?.join(","),
      status: selectedValues?.status?.join(","),
      flagged: ((selectedValues?.flagged as string[]) || []).includes("true")
        ? "true"
        : "",
      searchValue: search,
    });
  }, [sortMeta]);

  // Refetch when period changes
  useEffect(() => {
    console.log("[MyProcesses] period changed", period);
    setPage(0);
    setRows(10);
    fetchData({
      pageIndex: 0,
      pageSize: 10,
      service: selectedValues?.service_line?.join(","),
      status: selectedValues?.status?.join(","),
      flagged: ((selectedValues?.flagged as string[]) || []).includes("true")
        ? "true"
        : "",
      searchValue: search,
    });
  }, [period]);

  useEffect(() => {
    setPage(0);
    setRows(10);
    fetchData({
      pageIndex: 0,
      pageSize: 10,
      service: selectedValues?.service_line?.join(","),
      status: selectedValues?.status?.join(","),
      flagged: ((selectedValues?.flagged as string[]) || []).includes("true")
        ? "true"
        : "",
      searchValue: debouncedSearchTerm,
    });
  }, [debouncedSearchTerm]);

  const onPageChange = ({ page, rows }) => {
    setPage(page);
    setRows(rows);
    fetchData({
      pageIndex: page,
      pageSize: rows,
      service: filter?.service_line?.join(","),
      status: filter?.status?.join(","),
      flagged: ((filter?.flagged as string[]) || []).includes("true")
        ? "true"
        : "",
      searchValue: search,
    });
  };

  const onSort = (e) => {
    setSortMeta(e.multiSortMeta);
  };

  const applyFilters = () => {
    setPage(0);
    setRows(10);
    fetchData({
      pageIndex: 0,
      pageSize: 10,
      service: selectedValues?.service_line?.join(",") || "",
      status: selectedValues?.status?.join(",") || "",
      flagged: ((selectedValues?.flagged as string[]) || []).includes("true")
        ? "true"
        : "",
      searchValue: search,
    });
    setFilter(selectedValues);
    handleChipData(selectedValues);
  };

  const refetchWithCurrentFilters = () => {
    fetchData({
      pageIndex: 0,
      pageSize: 10,
      service: selectedValues?.service_line?.join(",") || "",
      status: selectedValues?.status?.join(",") || "",
      flagged: ((selectedValues?.flagged as string[]) || []).includes("true")
        ? "true"
        : "",
      searchValue: search,
    });
  };

  const deSelectAll = () => {
    setPage(0);
    setRows(10);
    setFilter({
      status: [],
      service_line: [],
      flagged: [],
    });
    setSelectedFilters({
      status: [],
      service_line: [],
      flagged: [],
    });
    setSelectedValues({
      status: [],
      service_line: [],
      flagged: [],
    });
    setChipData([]);
    fetchData({
      pageIndex: 0,
      pageSize: 10,
      service: "",
      status: "",
      flagged: "",
      searchValue: search,
    });
  };
  const handleRemoveFilter = (data) => {
    const array = selectedValues[data.category]?.filter((value) => {
      return value !== data?.value;
    });

    delete selectedFilters[data?.value];
    setSelectedFilters(selectedFilters);
    const filteredValue = {
      ...selectedValues,
      [data.category]: array,
    };
    setPage(0);
    setRows(10);
    setFilter(filteredValue);
    handleChipData(filteredValue);
    setSelectedValues(filteredValue);
    fetchData({
      pageIndex: 0,
      pageSize: 10,
      service: filteredValue?.service_line?.join(",") || "",
      status: filteredValue?.status?.join(",") || "",
      flagged: ((filteredValue?.flagged as string[]) || []).includes("true")
        ? "true"
        : "",
      searchValue: search,
    });
  };
  const getStatus = (code) => {
    return statusOption.find((data) => data?.code === code);
  };
  const getService = (id) => {
    return entityservices?.find((data) => data?.id === id);
  };
  const shouldHideServiceFilter =
    accessServices.length <= 1 && allEntities.length <= 1;

  return {
    data,
    loading,
    isDataAvailable,
    totalRecord,
    onPageChange,
    rows,
    page,
    sortMeta,
    onSort,
    chipData,
    setChipData,
    selectedFilters,
    setSelectedFilters,
    applyFilters,
    selectedValues,
    setSelectedValues,
    deSelectAll,
    serviceSubOptions,
    handleRemoveFilter,
    getStatus,
    statusOption,
    getService,
    shouldHideServiceFilter,
    searchQuery: search,
    setSearchQuery: setSearch,
    period,
    setPeriod,
    refetchWithCurrentFilters,
  };
};

export default useMyProcesses;
